import { get } from 'svelte/store';
import {
	bearerToken,
	apiError,
	offlineMode,
	currentRestaurant,
	currentDevice,
	rememberMe,
	phpSessId
} from 'Services/store';
import { browser } from '$app/environment';
import { env } from '$env/dynamic/public';
import { cloneDeep } from './lodash';

const ERROR_CODE_NO_RESPONSE = [503, 504, 522];
const ERRORS_CODE_NO_RETRY = [400, 401, 403, 404, 405, 412, 422, 500, 501, 406];

const DELAY_BETWEEN_CALLS = 550;

// This custom user agent is only used when calling the API from the server side
// It will be used by the API to disable throttling.
export const SERVER_SIDE_USER_AGENT = 'MGNT-Sveltekit-serverside';

const defaultParams = {
	allowRedirection: true,
	redirectionUrl: null,
	getRequestObject: false,
	getRequestStatus: false,
	maxRetries: 5,
	retry: true,
	showUser: false,
	isApiPlatform: false
};

export const jsonHeaders = {
	headers: {
		Accept: '*/*',
		'Content-Type': 'application/json'
	}
};

export class FetchApiKitAbortError extends Error {
	constructor() {
		super('FetchApiKit: Aborted');
	}
}

export class FetchApiKitFailToFetchException extends Error {
	constructor() {
		super('FetchApiKit: Failed to fetch');
	}
}

export const getApiUrl = (isApiPlatform) => {
	if (isApiPlatform) {
		return browser
			? `${window?.location?.origin}/api`
			: `http://${env.SERVER_API_PLATFORM_HOST}/api`;
	}
	return browser ? window?.location?.origin + env.CLIENT_API_PATH : env.SERVER_API_PATH;
};

/**
 * @param {Response} response
 * @param {boolean} [responseObjectIsRequested]
 * @param {boolean} [responseStatusIsRequested]
 */
export async function getResponseData(
	response,
	responseObjectIsRequested = false,
	responseStatusIsRequested = false
) {
	if (responseObjectIsRequested) {
		return responseStatusIsRequested ? [response, response.status] : response;
	}
	try {
		const json = await response.json();
		return responseStatusIsRequested ? [json, response.status] : json;
	} catch (errResponseJson) {
		try {
			const text = await response.text();
			return responseStatusIsRequested ? [text, response.status] : text;
		} catch (errResponseText) {
			return responseStatusIsRequested ? [null, response.status] : null;
		}
	}
}

/**
 * @typedef {Object} CustomRequest
 * @property {string} route
 * @property {"GET" | "POST" | "DELETE" | "PATCH" | "PUT"} [method]
 * @property {Record<string, any>} [headers]
 * @property {Record<string, any>} [body]
 */

/**
 *
 * @param {CustomRequest} request
 * @param {typeof defaultParams} params
 * @returns
 */
export async function fetchApiKit(request, options = {}, tryCount = 1) {
	const params = { ...defaultParams, ...options };

	const apiURL = getApiUrl(params.isApiPlatform);

	const originRequest = cloneDeep(request);
	request.route = apiURL + request.route;

	if (browser) {
		// Set the authorization bearer if not already done
		if (!request.headers?.Authorization?.includes('Bearer') && get(bearerToken)) {
			request.headers = {
				...request.headers,
				Authorization: `Bearer ${get(bearerToken)}`
			};
		}
		if (!request.headers?.['Access-Control-Allow-Origin']) {
			request.headers = {
				...request.headers,
				'Access-Control-Allow-Origin': '*'
			};
		}

		const cookiesArr = (request.headers?.Cookie || '').split(';').map((cookie) => cookie.trim());

		if (!request.headers?.Cookie?.includes('PHPSESSID') && get(phpSessId)) {
			cookiesArr.push(`PHPSESSID=${get(phpSessId)}`);
			if (get(rememberMe)) {
				cookiesArr.filter((cookie) => !cookie.includes('REMEMBERME='));
				cookiesArr.push(`REMEMBERME=${get(rememberMe)}`);
			}
		}

		// Set the restaurantToken from store if not already in the cookies (for server-side fetchs)
		// FIXME : add the temp token for groups
		if (get(currentRestaurant)?.token) {
			if (!request.headers?.Cookie?.includes('restaurantToken')) {
				cookiesArr.push(`restaurantToken=${get(currentRestaurant).token}`);
			}
		}
		if (get(currentDevice)?.token) {
			if (!request.headers?.Cookie?.includes('deviceToken')) {
				cookiesArr.push(`deviceToken=${get(currentDevice).token}`);
			}
		}

		if (cookiesArr.length) {
			request.headers = {
				...request.headers,
				Cookie: cookiesArr.join('; ')
			};
		}
	} else {
		request.headers = { ...(request?.headers || {}), 'user-agent': SERVER_SIDE_USER_AGENT };
	}

	const fetchParams = {
		credentials: 'include',
		method: request.method || 'GET'
	};

	if (request.body) {
		fetchParams.body = request.body;
	}
	if (Object.keys(request?.headers || {}).length) {
		fetchParams.headers = request.headers;
	}

	try {
		const requestParams = {
			credentials: 'include',
			headers: request.headers || {},
			method: request.method || 'GET',
			body: request.body || null,
			signal: options?.signal || null
		};

		const response = params?.fetch
			? await params.fetch(request.route, requestParams) // most likely server-side
			: await fetch(request.route, requestParams); // most likely client-side

		if (!response) {
			throw Error(`No Response fetching ${request.route}`);
		}

		offlineMode.update((v) => null);

		if (response.ok) {
			return getResponseData(response, params.getRequestObject, params.getRequestStatus);
		}

		// If the status is not OK (not 200)
		if (ERROR_CODE_NO_RESPONSE.includes(response.status)) {
			// TODO : Handle offline mode
		}

		// If the status code is not in the codes to retries
		if (ERRORS_CODE_NO_RETRY.includes(response.status) || !params.retry) {
			const reponseData = await getResponseData(
				response,
				params.getRequestObject,
				params.getRequestStatus
			);
			return Promise.reject(reponseData);
		}

		// Retry the request
		if (tryCount < params.maxRetries) {
			return new Promise((resolve, reject) => {
				setTimeout(async () => {
					try {
						const retryR = await fetchApiKit(originRequest, params, tryCount + 1);
						resolve(retryR);
					} catch (retryError) {
						reject(retryError);
					}
				}, DELAY_BETWEEN_CALLS);
			});
		}

		// throw Error(`${response.status} ${response.statusText}`)

		const allRetryFailsResponse = await getResponseData(
			response,
			params.getRequestObject,
			params.getRequestStatus
		);
		return Promise.reject(allRetryFailsResponse);
	} catch (error) {
		if (error.name === 'AbortError') {
			throw new FetchApiKitAbortError();
		}
		if (error == 'TypeError: NetworkError when attempting to fetch resource.') {
			offlineMode.update((v) => (new Date(v).getTime() > 0 ? v : Date.now()));
			throw error;
		} else if (error == 'TypeError: Failed to fetch') {
			console.log(
				'failed to fetch from fetchApiKit :' + request?.route || '<unknown route>',
				JSON.stringify(request?.headers, null, 2)
			);
			throw new FetchApiKitFailToFetchException();
		} else {
			console.error(
				'fetch error from fetchApiKit',
				error,
				request?.headers?.Cookie,
				'route: ',
				request.route
			);
			if (params.showUser) {
				apiError.set({
					show: true,
					message: error,
					code: ''
				});
				return null;
			}
			if (params.allowRedirection && params.redirectionUrl) {
				// FIXME no window in kit due to SSR
				if (browser) window.location.href = params.redirectionUrl;
			}
			throw error;
		}
	}
}
